import { readFileText, setupMarkdownToolPage } from './shared/index.js';
import {
  HTML_MAX_BYTES,
  validateHtmlText,
} from './formats/html/convert.js';
import HtmlWorker from './formats/html/worker.js?worker';

const WORKER_TIMEOUT_MS = 30_000;

function sourceEditor(root) {
  return root.querySelector('#source-text, #html-source, textarea[name="source"]');
}

function control(root, selectors) {
  return root.querySelector(selectors);
}

function runWorker(WorkerConstructor, payload, signal) {
  if (typeof Worker === 'undefined') return Promise.reject(new Error('This browser cannot start the local conversion worker.'));
  if (signal?.aborted) return Promise.reject(new DOMException('Conversion canceled.', 'AbortError'));
  return new Promise((resolve, reject) => {
    const worker = new WorkerConstructor({ name: 'kivoza-html-to-markdown' });
    let settled = false;
    let timeoutId;
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', cancel);
      worker.terminate();
      callback(value);
    };
    const cancel = () => finish(reject, new DOMException('Conversion canceled.', 'AbortError'));
    timeoutId = setTimeout(() => finish(reject, new Error('HTML conversion timed out after 30 seconds.')), WORKER_TIMEOUT_MS);
    signal?.addEventListener('abort', cancel, { once: true });
    worker.onmessage = (event) => {
      if (event.data?.ok) finish(resolve, event.data.result);
      else finish(reject, new Error(event.data?.error || 'HTML conversion failed.'));
    };
    worker.onerror = (event) => finish(reject, new Error(event.message || 'HTML conversion worker failed.'));
    worker.postMessage(payload);
  });
}

/** Initialize the HTML page. The page owns only source/input controls. */
export function initHtmlPage(root = document) {
  const source = sourceEditor(root);
  const sourceMode = control(root, '#html-source-mode, select[name="sourceMode"]');
  const keepLinks = control(root, '#html-keep-links, input[name="keepLinks"]');
  const stripEmpty = control(root, '#html-strip-empty, input[name="stripEmpty"]');
  let sourceFile = null;
  let reading = false;
  const page = setupMarkdownToolPage({
    root,
    defaultFileName: 'html-document',
    onFiles: async (files) => {
      const file = files?.[0];
      if (!file) return;
      if (files.length > 1) { page.setStatus('Choose one HTML file at a time.', 'error'); return; }
      if (!/\.html?$/i.test(file.name) && file.type && !/html/i.test(file.type)) {
        page.setStatus('Choose an HTML or HTM file.', 'error');
        return;
      }
      if (reading) return;
      page.setReady(false);
      reading = true;
      try {
        const text = await readFileText(file, HTML_MAX_BYTES, 'HTML file');
        sourceFile = file;
        if (source) source.value = text;
        page.setReady(true);
        page.setStatus(`${file.name} is ready. Review the source, then convert.`, 'success');
      } catch (error) {
        sourceFile = null;
        page.setStatus(error instanceof Error ? error.message : 'The HTML file could not be read.', 'error');
      } finally {
        reading = false;
      }
    },
  });
  const convertButton = page.elements.convertButton;
  convertButton?.addEventListener('click', () => {
    const text = source?.value || '';
    const problem = validateHtmlText(text);
    if (problem) { page.setStatus(problem, 'error'); return; }
    page.result.clear();
    page.runConversion(async ({ signal, report }) => {
      report('Parsing HTML locally…', 0.2);
      const result = await runWorker(HtmlWorker, {
        source: text,
        options: {
          sourceMode: sourceMode?.value || 'fragment',
          keepLinks: keepLinks ? Boolean(keepLinks.checked) : true,
          stripEmpty: stripEmpty ? Boolean(stripEmpty.checked) : true,
        },
      }, signal);
      report('Markdown is ready.', 1);
      return result;
    }).catch(() => {});
  });
  page.elements.clearButton?.addEventListener('click', () => {
    sourceFile = null;
    if (source) source.value = '';
    page.setReady(false);
    page.result.clear();
    page.setStatus('Source cleared.');
  });
  source?.addEventListener('input', () => {
    sourceFile = null;
    const problem = validateHtmlText(source.value);
    page.setReady(!problem);
    if (!problem) page.setStatus('Source edited locally.');
  });
  page.setReady(Boolean(source?.value?.trim()) && !validateHtmlText(source.value));
  return { page, get sourceFile() { return sourceFile; } };
}

if (typeof document !== 'undefined' && document.documentElement.dataset.markdownFormat === 'html') initHtmlPage();
